import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {CommonModule, formatDate} from '@angular/common';
import {DxDataGridComponent, DxFormComponent, DxTemplateDirective} from 'devextreme-angular';
import {
    DxiColumnComponent,
    DxiItemComponent,
    DxoColumnChooserComponent,
    DxoExportComponent,
    DxoFilterRowComponent,
    DxoHeaderFilterComponent,
    DxoLoadPanelComponent,
    DxoPagerComponent,
    DxoPagingComponent,
    DxoPositionComponent,
    DxoScrollingComponent,
    DxoSearchComponent,
    DxoSelectionComponent,
    DxoSortingComponent,
    DxoToolbarComponent,
} from 'devextreme-angular/ui/nested';
import {CustomStore} from 'devextreme-aspnet-data-nojquery';
import {capitalCase} from 'change-case';
import {FilterPanelComponent} from '../../../components/library/filter-panel/filter-panel.component';
import {FilterConfig, GridPageComponent} from '../../common/grid-page.component';
import {NestleStatus, PositionStatus, UserHistoryField, UserHistoryGridViewDto, UserHistorySection, UserStatus} from '@services';
import EnumUtils from '../../../shared/enum-utils';
import {UserAvatarEmailComponent} from '../../../components/utils/user-avatar-email/user-avatar-email.component';
import {PositionStatusPipe} from '../../../pipes/position-status.pipe';
import {NestleStatusPipe} from '../../../pipes/nestle-status.pipe';
import {formatPhone} from '../../../pipes/phone.pipe';

@Component({
    selector: 'app-user-history',
    imports: [
        CommonModule,
        DxDataGridComponent,
        DxoScrollingComponent,
        DxoPagingComponent,
        DxoPagerComponent,
        DxoLoadPanelComponent,
        DxoSortingComponent,
        DxoHeaderFilterComponent,
        DxoFilterRowComponent,
        DxoColumnChooserComponent,
        DxoPositionComponent,
        DxoSearchComponent,
        DxoSelectionComponent,
        DxoExportComponent,
        DxoToolbarComponent,
        DxiItemComponent,
        DxiColumnComponent,
        DxTemplateDirective,
        DxFormComponent,
        FilterPanelComponent,
        UserAvatarEmailComponent,
    ],
    templateUrl: './user-history.component.html',
})
export class UserHistoryComponent extends GridPageComponent<UserHistoryGridViewDto> implements OnChanges {
    @Input() employeeId: string | null = null;
    @Input() offset: number = 157;
    @Input() embedded: boolean = false;

    protected override exportName: string = 'User History';
    readonly dateTimeFormat = 'MMMM d, y, h:mm a';
    dataSource: CustomStore | UserHistoryGridViewDto[] = [];

    private readonly positionStatusPipe = new PositionStatusPipe();
    private readonly nestleStatusPipe = new NestleStatusPipe();

    private readonly sectionLabels: Record<string, string> = {
        [UserHistorySection.Contact]: 'Contact',
        [UserHistorySection.EmergencyDetails]: 'Emergency Details',
        [UserHistorySection.ShiftDetails]: 'Shift Details',
        [UserHistorySection.FinancialDetails]: 'Financial Details',
        [UserHistorySection.EmploymentDetails]: 'Employment Details',
        [UserHistorySection.NestleDetails]: 'Nestle Details',
        [UserHistorySection.SecurityAndPermissions]: 'Security & Permissions',
        [UserHistorySection.SupplierDetails]: 'Supplier Details',
        [UserHistorySection.CarrierDetails]: 'Carrier Details',
        [UserHistorySection.VendorDetails]: 'Vendor Details',
        [UserHistorySection.CustomerDetails]: 'Customer Details',
        [UserHistorySection.SiteDetails]: 'Site Details',
        [UserHistorySection.AgencyDetails]: 'Agency Details',
        [UserHistorySection.IssuePassword]: 'Issue Password',
    };

    private readonly fieldLabels: Record<string, string> = {
        [UserHistoryField.FirstName]: 'First Name',
        [UserHistoryField.MiddleName]: 'Middle Name',
        [UserHistoryField.LastName]: 'Last Name',
        [UserHistoryField.AlternativeFirstName]: 'Alternative First Name',
        [UserHistoryField.AlternativeLastName]: 'Alternative Last Name',
        [UserHistoryField.Email]: 'Email Address',
        [UserHistoryField.Phone]: 'Phone Number',
        [UserHistoryField.Address]: 'Address',
        [UserHistoryField.EmergencyContactName]: 'Emergency Contact Name',
        [UserHistoryField.EmergencyContactNumber]: 'Emergency Contact Number',
        [UserHistoryField.MedicalConditions]: 'Medical Conditions',
        [UserHistoryField.Shift]: 'Shift',
        [UserHistoryField.Supervisor]: 'Supervisor',
        [UserHistoryField.RegularPayRateCode]: 'Pay Rate Code',
        [UserHistoryField.RegularPayRateAmount]: 'Pay Rate Amount',
        [UserHistoryField.RegularPayEffectiveDate]: 'Regular Pay Effective Date',
        [UserHistoryField.AnnualSalary]: 'Annual Salary',
        [UserHistoryField.PayCycleDescription]: 'Pay Cycle Description',
        [UserHistoryField.PositionStatus]: 'Position Status',
        [UserHistoryField.PositionId]: 'Position ID',
        [UserHistoryField.PayrollCompanyCode]: 'Payroll Company Code',
        [UserHistoryField.TaxId]: 'Tax ID (SIN)',
        [UserHistoryField.BirthDate]: 'Birth Date',
        [UserHistoryField.Age]: 'Age',
        [UserHistoryField.HireDate]: 'Hire Date',
        [UserHistoryField.TerminationDate]: 'Termination Date',
        [UserHistoryField.JobTitleDescription]: 'Job Title Description',
        [UserHistoryField.PayClass]: 'Pay Class',
        [UserHistoryField.PayDivision]: 'Pay Division',
        [UserHistoryField.PaidPosition]: 'Paid Position',
        [UserHistoryField.HomeDepartmentCode]: 'Home Department Code',
        [UserHistoryField.HomeCostNumberCode]: 'Home Cost Number Code',
        [UserHistoryField.ProvinceOfEmployment]: 'Province of Employment',
        [UserHistoryField.StandardHours]: 'Standard Hours',
        [UserHistoryField.SapAccess]: 'SAP Access',
        [UserHistoryField.DwaAccess]: 'DWA Access',
        [UserHistoryField.NextViewAccess]: 'NextView Access',
        [UserHistoryField.NestleStatus]: 'Nestle Status',
        [UserHistoryField.AdpHireDate]: 'ADP Hire Date',
        [UserHistoryField.NewHire]: 'New Hire',
        [UserHistoryField.NestleAgency]: 'Agency',
        [UserHistoryField.NestlePositionId]: 'Position ID',
        [UserHistoryField.SapSuccessFactorId]: 'SAP/Success Factor ID',
        [UserHistoryField.EmployeeId]: 'Employee ID',
        [UserHistoryField.NestleUserId]: 'User ID',
        [UserHistoryField.NestleEmail]: 'Email',
        [UserHistoryField.NextPasswordExpiryDate]: 'Next Password Expiry Date',
        [UserHistoryField.Status]: 'Status',
        [UserHistoryField.Roles]: 'User Role',
        [UserHistoryField.NotifyByEmail]: 'Notify by Email',
        [UserHistoryField.NotifyByPhone]: 'Notify by Phone',
        [UserHistoryField.TwoFactorAuthentication]: 'Two-Factor Authentication',
        [UserHistoryField.Suppliers]: 'Supplier',
        [UserHistoryField.Carriers]: 'Carrier',
        [UserHistoryField.Vendors]: 'Vendor',
        [UserHistoryField.Customers]: 'Customer ID',
        [UserHistoryField.Sites]: 'Site ID',
        [UserHistoryField.Agencies]: 'Agency ID',
        [UserHistoryField.Password]: 'Password',
    };

    private readonly dateFields = new Set<UserHistoryField>([
        UserHistoryField.RegularPayEffectiveDate,
        UserHistoryField.BirthDate,
        UserHistoryField.HireDate,
        UserHistoryField.TerminationDate,
        UserHistoryField.AdpHireDate,
        UserHistoryField.NextPasswordExpiryDate,
    ]);

    private readonly phoneFields = new Set<UserHistoryField>([
        UserHistoryField.Phone,
        UserHistoryField.EmergencyContactNumber,
    ]);

    sectionOptions = EnumUtils.getEnumOptions(UserHistorySection, (v) => this.sectionLabel(v));
    fieldOptions = EnumUtils.getEnumOptions(UserHistoryField, (v) => this.fieldLabel(v));

    protected override filterConfig: FilterConfig[] = [
        {field: 'employee.name', op: 'contains', customFilter: this.nameOrEmailFilter},
        {field: 'created', op: '>='},
        {field: 'section', op: '='},
        {field: 'field', op: '='},
    ];

    constructor() {
        super();
        this.init(UserHistoryGridViewDto);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['employeeId'] && this.employeeId) {
            this.dataSource = this.dataSources.userHistory(this.employeeId);
        }
    }

    sectionLabel(section: UserHistorySection | string | undefined | null): string {
        return section != null ? (this.sectionLabels[section] ?? String(section)) : '';
    }

    fieldLabel(field: UserHistoryField | string | undefined | null): string {
        return field != null ? (this.fieldLabels[field] ?? String(field)) : '';
    }

    badgeClass(field: UserHistoryField | undefined, value: string): string | null {
        switch (field) {
            case UserHistoryField.Status: return EnumUtils.getUserStatusBadgeClass(value as UserStatus);
            case UserHistoryField.PositionStatus: return EnumUtils.getPositionStatusBadgeClass(value as PositionStatus);
            case UserHistoryField.NestleStatus: return EnumUtils.getNestleStatusBadgeClass(value as NestleStatus);
            default: return null;
        }
    }

    displayValue(field: UserHistoryField | undefined, value: string | undefined | null): string {
        if (!value) return 'Blank';
        if (field == null) return value;
        if (this.dateFields.has(field)) return formatDate(value, 'longDate', 'en-US');
        if (this.phoneFields.has(field)) return formatPhone(value);

        switch (field) {
            case UserHistoryField.PositionStatus: return this.positionStatusPipe.transform(value);
            case UserHistoryField.NestleStatus: return this.nestleStatusPipe.transform(value);
            case UserHistoryField.JobTitleDescription: return capitalCase(value);
            case UserHistoryField.PayrollCompanyCode: return value.toUpperCase();
            case UserHistoryField.PayClass: return capitalCase(value).toUpperCase();
            default: return value;
        }
    }

    calculateChangeCellValue = (row: UserHistoryGridViewDto): string =>
        `${this.fieldLabel(row.field)}: ${this.displayValue(row.field, row.oldValue)} → ${this.displayValue(row.field, row.newValue)}`;
}
